import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { ArrowLeft, Loader2, Mail, Phone, MapPin, Building2, Calendar, Briefcase, Edit2 } from "lucide-react";
import EmployeeDocuments from "@/components/EmployeeDocuments";
import EditEmployeeModal from "@/components/EditEmployeeModal";

const TABS = ["Personal", "Contact", "Employment", "Bank", "Statutory"];

export default function EmployeeProfile() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("Personal");
  const [payroll, setPayroll] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = () => {
    api.get(`/employees/${id}`).then((r) => setEmp(r.data));
    api.get("/payroll", { params: { employee_id: id } }).then((r) => setPayroll(r.data));
  };

  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDepartments(r.data));
  }, [id]);

  if (!emp) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="employee-profile">
      <Link to="/employees" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to Directory
      </Link>

      <div className="card-flat flex items-center gap-6 flex-wrap">
        <img src={emp.photo_url} alt={emp.full_name} className="w-24 h-24 rounded-full object-cover ring-4 ring-primary/10" />
        <div className="flex-1 min-w-[200px]">
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>{emp.employee_code}</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1" data-testid="employee-name">{emp.full_name}</h1>
          <div className="text-stone-600 mt-1">{emp.designation} · {emp.department_name}</div>
          <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
            <span className="status-pill" style={{ background: "var(--color-primary-light)", color: "var(--color-primary)" }}>{emp.status}</span>
            <span className="status-pill" style={{ background: "#F4F1DE", color: "#57534E" }}>{emp.grade}</span>
            <span className="status-pill" style={{ background: "#FADED7", color: "#9A3412" }}>{emp.employment_type}</span>
          </div>
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <button onClick={() => setEditing(true)} data-testid="edit-employee-button"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-white"
                  style={{ background: "var(--color-primary)" }}>
            <Edit2 className="w-3.5 h-3.5" /> Edit Profile
          </button>
          <div>
            <div className="kpi-label">Annual CTC</div>
            <div className="font-heading text-2xl font-bold mt-1" style={{ color: "var(--color-primary)" }}>{formatINR(emp.ctc_annual)}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-stone-200 overflow-x-auto" data-testid="profile-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
                  className={`px-5 py-3 text-sm font-medium transition border-b-2 ${tab === t ? "border-primary text-primary" : "border-transparent text-stone-500 hover:text-stone-900"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="card-flat">
        {tab === "Personal" && (
          <Grid items={[
            ["Date of Birth", emp.dob], ["Gender", emp.gender], ["Blood Group", emp.blood_group],
            ["Marital Status", emp.marital_status], ["Aadhaar", emp.aadhaar], ["PAN", emp.pan],
          ]} />
        )}
        {tab === "Contact" && (
          <Grid items={[
            ["Mobile", emp.mobile], ["Email", emp.email],
            ["Current Address", emp.current_address], ["Permanent Address", emp.permanent_address],
          ]} />
        )}
        {tab === "Employment" && (
          <Grid items={[
            ["Joining Date", emp.joining_date], ["Confirmation Date", emp.confirmation_date],
            ["Department", emp.department_name], ["Designation", emp.designation],
            ["Reporting Manager", emp.reporting_manager], ["Grade", emp.grade],
            ["Employment Type", emp.employment_type],
          ]} />
        )}
        {tab === "Bank" && (
          <Grid items={[
            ["Bank Name", emp.bank_name], ["Account Number", emp.account_number],
            ["IFSC", emp.ifsc], ["Branch", emp.branch],
          ]} />
        )}
        {tab === "Statutory" && (
          <Grid items={[
            ["PF Number", emp.pf_number], ["UAN", emp.uan],
            ["ESI Number", emp.esi_number], ["Professional Tax", "₹200/month"],
          ]} />
        )}
      </div>

      <EmployeeDocuments employeeId={emp.id} />

      <div className="card-flat">
        <div className="kpi-label">Salary History</div>
        <h3 className="font-heading text-xl font-bold mt-1 mb-4">Recent Payroll</h3>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-stone-500 text-xs uppercase tracking-wider border-b border-stone-200">
            <th className="py-2">Month</th><th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Status</th>
          </tr></thead>
          <tbody>
            {payroll.length === 0 && <tr><td colSpan="5" className="py-6 text-center text-stone-500">No payroll records</td></tr>}
            {payroll.map((p) => (
              <tr key={p.id} className="border-b border-stone-100">
                <td className="py-3">{p.month}</td>
                <td>{formatINR(p.gross)}</td>
                <td className="text-orange-700">-{formatINR(p.total_deductions)}</td>
                <td className="font-semibold">{formatINR(p.net_pay)}</td>
                <td><span className="status-pill" style={{ background: "var(--color-primary-light)", color: "var(--color-primary)" }}>{p.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditEmployeeModal employee={emp} departments={departments}
                           onClose={() => setEditing(false)}
                           onSaved={(updated) => { setEmp(updated); setEditing(false); load(); }} />
      )}
    </div>
  );
}

function Grid({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-10">
      {items.map(([k, v]) => (
        <div key={k}>
          <div className="kpi-label mb-1">{k}</div>
          <div className="text-sm font-medium">{v || "—"}</div>
        </div>
      ))}
    </div>
  );
}
